import { barX, defineChart } from "@tanstack/charts";
import { scaleBand } from "@tanstack/charts/scales/band";
import { scaleLinear } from "@tanstack/charts/scales/linear";
import { BrandChartTheme } from "../BrandChart";
import { Side, TornadoChartDataSide, TornadoChartItem } from "./TornadoChart.types";

export const ROW_PADDING = 0.2;

/** Row order follows the side the user sorted by. */
export const getTornadoRows = (
  [left, right]: [TornadoChartDataSide, TornadoChartDataSide],
  sortSide: Side,
): TornadoChartItem[] => {
  const sorted = sortSide === "left" ? left : right;

  // Each side's items carry both scores; look the row up on the sorted side.
  return sorted.items.map((item) => ({
    name: item.name,
    leftScore: item.leftScore,
    rightScore: item.rightScore,
  }));
};

/**
 * One half of the tornado. The left half runs its value axis right to left so
 * both halves grow outwards from the centre labels. Rows use a configured
 * band scale so both halves (and the HTML labels) share the same order.
 */
export const buildTornadoSideDefinition = ({
  rows,
  side,
  maxReferenceScore,
  brandTheme,
  fill,
}: {
  rows: TornadoChartItem[];
  side: Side;
  maxReferenceScore: number;
  brandTheme: BrandChartTheme;
  fill: string;
}) => {
  const max = Math.max(maxReferenceScore, 1);

  return defineChart({
    theme: brandTheme.theme,
    margin: { top: 0, right: 0, bottom: 0, left: 0 },
    marks: [
      barX(rows, {
        x: side === "left" ? "leftScore" : "rightScore",
        y: "name",
        key: "name",
        fill,
        radius: { end: 3 },
      }),
    ],
    scales: {
      x: {
        // Configured instances keep fixed domains; factories re-infer them.
        scale: scaleLinear().domain(side === "left" ? [max, 0] : [0, max]),
        axis: false,
      },
      y: {
        scale: scaleBand<string>()
          .domain(rows.map((row) => row.name))
          .paddingInner(ROW_PADDING)
          .paddingOuter(ROW_PADDING / 2),
        axis: false,
      },
    },
  });
};
